import { TransactionStats } from '../components/tax-reports/types';
import { environment } from '../config/environment';
import { useApiClient } from './api';
import { TransactionTypeGroup } from './filter.types';
import { ApiResult } from './general.types';
import {
    CoinWithSymbol,
    RecalculateData,
    SpamCenterCoin,
    Transaction,
    TransactionGroup,
    TransactionIssue,
    TransactionsFilterData,
    TransactionType,
    TransactionTypeValue,
} from './transaction.types';
import { removeUnsupportedTypes, transformTransaction } from './transformers';
import i18n from 'i18next';
import { translateTransactionTypes } from '../utils/transactionTypeTranslations';

export enum DetailsType {
    NONE = 'false',
    FULL = 'true',
    ONLY_ISSUES = 'only_issues',
}

export const useTransactionService = () => {
    const apiClient = useApiClient();

    const get = async (
        page: number,
        size: number,
        filterData: TransactionsFilterData | undefined,
        sortDirection: string,
        sortBy: string,
        signal?: AbortSignal
    ) => {
        try {
            const params = new URLSearchParams();
            params.append('page', page.toString());
            params.append('size', size.toString());
            params.append('sortDirection', sortDirection);
            params.append('sortBy', sortBy);

            if (filterData) {
                if (filterData.search) params.append('search', filterData.search);
                if (filterData.types?.length)
                    filterData.types.forEach((type) => params.append('types[]', type.toString()));
                if (filterData.accounts?.length)
                    filterData.accounts.forEach((account) => params.append('customNames[]', account));
                if (filterData.coins?.length) filterData.coins.forEach((coin) => params.append('coinTickers[]', coin));
                if (filterData.startDate) {
                    // Convert to start of day timestamp
                    const startTimestamp = Math.floor(new Date(filterData.startDate + 'T00:00:00').getTime() / 1000);
                    params.append('startDate', startTimestamp.toString());
                }
                if (filterData.endDate) {
                    // Convert to end of day timestamp
                    const endTimestamp = Math.floor(new Date(filterData.endDate + 'T23:59:59').getTime() / 1000);
                    params.append('endDate', endTimestamp.toString());
                }
                // Completely revert to original implementation
                if (filterData.reviewTypes?.length) {
                    filterData.reviewTypes.forEach((reviewType) =>
                        params.append('reviewTypes[]', reviewType.toString())
                    );
                }
            }

            // Log the exact URL parameters being sent to backend
            const urlParams = new URLSearchParams(params);
            console.log(`🌐 [Transaction Service] API CALL DEBUG:`, {
                baseUrl: `${environment.ctLiteApiUrl}/transaction`,
                queryString: urlParams.toString(),
                parsedParams: Object.fromEntries(urlParams.entries()),
                endDateCheck: params.get('endDate') ? `endDate=${params.get('endDate')} (${new Date(parseInt(params.get('endDate')!) * 1000).toISOString()})` : 'NO endDate'
            });

            const response = await apiClient.get<ApiResult<Transaction[]>>(`${environment.ctLiteApiUrl}/transaction`, {
                params,
                signal,
            });
            return transformTransaction(response.data);
        } catch (error) {
            console.error(error);
            throw error;
        }
    };

    const getCount = async () => {
        try {
            const response = await apiClient.get<any>(`${environment.ctLiteApiUrl}/transaction/count`);
            return response.data;
        } catch (error) {
            console.error(error);
            throw error;
        }
    };

    const getUserAccounts = async () => {
        try {
            const response = await apiClient.get<string[]>(`${environment.ctLiteApiUrl}/transaction/user-accounts`);
            return response.data;
        } catch (error) {
            console.error(error);
            throw error;
        }
    };

    const getLiquidityPools = async (): Promise<string[]> => {
        try {
            const response = await apiClient.get<ApiResult<string[]>>(
                `${environment.ctLiteApiUrl}/transaction/user-liquidity-pools`
            );
            return response.data.data;
        } catch (error) {
            console.error(error);
            throw error;
        }
    };

    const getUserCoins = async () => {
        try {
            const response = await apiClient.get<CoinWithSymbol[]>(
                `${environment.ctLiteApiUrl}/transaction/user-coins`
            );
            return response.data;
        } catch (error) {
            throw error;
        }
    };

    const getLiquidityPoolNames = async () => {
        try {
            const response = await apiClient.get<ApiResult<string[]>>(
                `${environment.ctLiteApiUrl}/transaction/liquidity-pool-names`
            );
            return response.data.data;
        } catch (error) {
            console.error(error);
            throw error;
        }
    };

    const getTypes = async () => {
        try {
            const response = await apiClient.get<TransactionType[]>(`${environment.ctLiteApiUrl}/transaction/types`);
            return translateTransactionTypes(removeUnsupportedTypes(response.data));
        } catch (error) {
            console.error(error);
            throw error;
        }
    };

    const getUserTypes = async () => {
        try {
            const response = await apiClient.get<TransactionType[]>(`${environment.ctLiteApiUrl}/transaction/user-types`);
            return translateTransactionTypes(removeUnsupportedTypes(response.data));
        } catch (error) {
            console.error(error);
            throw error;
        }
    };

    const getTypesGrouped = async (): Promise<TransactionTypeGroup[]> => {
        try {
            const types = await getTypes();
            if (!types) return [];

            // Group types by subgroupName
            const groupedMap = types.reduce((groups, type) => {
                const key = type.subgroupName;
                if (!groups.has(key)) {
                    groups.set(key, []);
                }
                groups.get(key)?.push(type);
                return groups;
            }, new Map<string, TransactionType[]>());

            // Convert map to array of TransactionTypeGroup
            return Array.from(groupedMap.entries()).map(([name, types]) => ({
                name,
                types,
            }));
        } catch (error) {
            console.error('Error getting grouped transaction types:', error);
            throw error;
        }
    };

    const getCoins = async (query: string) => {
        try {
            interface CoinApiResponse {
                coin: string;
                coinName: string;
                coinImage?: string;
                symbol: string;
            }

            const response = await apiClient.get<CoinApiResponse[]>(
                `${environment.ctLiteApiUrl}/transaction/coins?searchQuery=${query}`
            );

            // Map the API response to our CoinWithSymbol interface
            const coins: CoinWithSymbol[] = response.data.map((coin) => ({
                name: coin.coinName,
                ticker: coin.coin,
                image: coin.coinImage || '',
                active: true,
                symbol: coin.symbol || '',
            }));

            return coins;
        } catch (error) {
            throw error;
        }
    };

    const getFromTaxReports = async (page: number, limit: number, ctId: string) => {
        const params = new URLSearchParams();
        params.set('method', 'GET');
        params.set('path', `/reports/tax/validation/incomplete-transactions?user_id=${ctId}`);
        params.set(
            'body',
            JSON.stringify({
                user_id: ctId,
                page,
                limit,
                global_filter: '',
                sort_direction: [],
                currencies: 0,
                match_same_exchange: false,
                amount_accuracy: 90,
                time_accuracy: 2,
                already_matched: false,
                exchanges: [],
            })
        );

        try {
            const response = await apiClient.get<TransactionGroup[]>(
                `${environment.ctLiteApiUrl}/taxReportsUrl?${params.toString()}`
            );
            return response.data;
        } catch (error) {
            console.error(error);
            throw error;
        }
    };

    const save = async (transaction: Partial<Transaction>) => {
        try {
            //if transaction has id, it will update, otherwise insert
            await apiClient.post<void>(`${environment.ctLiteApiUrl}/transaction`, transaction);
        } catch (error) {
            console.error('Error saving transaction:', error);
            throw error;
        }
    };

    const groupSave = async (transactionIds: number[], data: any) => {
        try {
            await apiClient.post<void>(`${environment.ctLiteApiUrl}/transaction/group`, { transactionIds, data });
        } catch (error) {
            console.error(error);
            throw error;
        }
    };

    const deleteAllByIds = async (transactionIds: number[]) => {
        try {
            await apiClient.post<void>(`${environment.ctLiteApiUrl}/transaction/delete`, { transactionIds });
        } catch (error) {
            console.error(error);
            throw error;
        }
    };

    // Recalculate methods
    const recalculateInfo = async () => {
        try {
            const response = await apiClient.get<RecalculateData>(
                `${environment.ctLiteApiUrl}/transaction/recalc/status`
            );
            return response.data;
        } catch (error) {
            console.error(error);
            throw error;
        }
    };

    const recalculateStart = async () => {
        try {
            await apiClient.get<void>(`${environment.ctLiteApiUrl}/transaction/recalc/start`);
        } catch (error) {
            console.error(error);
            throw error;
        }
    };

    const recalculateStop = async () => {
        try {
            await apiClient.get<void>(`${environment.ctLiteApiUrl}/transaction/recalc/stop`);
        } catch (error) {
            console.error(error);
            throw error;
        }
    };

    const checkSpam = async () => {
        try {
            const response = await apiClient.post<SpamCenterCoin[]>(
                `${environment.ctLiteApiUrl}/transaction/check-spam`,
                {}
            );
            return response.data;
        } catch (error) {
            console.error(error);
            throw error;
        }
    };

    const markSpam = async (contractAddress: string, platformIdentifier: string, coin: string) => {
        try {
            const response = await apiClient.post<any>(`${environment.ctLiteApiUrl}/mark-spam`, null, {
                params: { contractAddress, csv: platformIdentifier, coin },
            });
            return response.data;
        } catch (error) {
            console.error(error);
            throw error;
        }
    };

    const groupMarkSpam = async (
        params: Array<{ contractAddress: string; platformIdentifier: string; coin: string }>
    ) => {
        try {
            await apiClient.post<void>(`${environment.ctLiteApiUrl}/transaction/group/mark-spam`, {
                spamParams: params.map((p) => ({
                    contractAddress: p.contractAddress,
                    csv: p.platformIdentifier,
                    coin: p.coin,
                })),
            });
        } catch (error) {
            console.error('Error marking transactions as spam:', error);
            throw error;
        }
    };

    const clearSpamTransactions = async (contractAddress: string, platformIdentifier: string) => {
        try {
            const response = await apiClient.post<any>(
                `${environment.ctLiteApiUrl}/transaction/clear-spam-transactions`,
                null,
                {
                    params: { contractAddress, csv: platformIdentifier },
                }
            );
            return response.data;
        } catch (error) {
            console.error(error);
            throw error;
        }
    };

    const groupRemoveSpam = async (
        params: Array<{ contractAddress: string; platformIdentifier: string; blacklistId: number }>
    ) => {
        try {
            await apiClient.post<void>(`${environment.ctLiteApiUrl}/transaction/group/remove-spam`, {
                spamParams: params.map((p) => ({
                    contractAddress: p.contractAddress,
                    csv: p.platformIdentifier,
                    blacklistId: p.blacklistId,
                })),
            });
        } catch (error) {
            console.error('Error marking transactions as spam:', error);
            throw error;
        }
    };

    const removeSpam = async (contractAddress: string, platformIdentifier: string, blacklistId: number) => {
        if (blacklistId === 0) {
            console.log('coin not spam center, but not in blacklist');
            return;
        }
        try {
            const response = await apiClient.post<any>(`${environment.ctLiteApiUrl}/transaction/remove-spam`, null, {
                params: { contractAddress, csv: platformIdentifier, rowId: blacklistId },
            });
            return response.data;
        } catch (error) {
            console.error(error);
            throw error;
        }
    };

    const getTransactionIssues: () => Promise<TransactionIssue[]> = async () => {
        try {
            const response = await apiClient.get<any>(`${environment.ctLiteApiUrl}/transaction/issues`);

            // Get current language from i18next if available
            const currentLang = i18n?.language || 'en';
            const isGerman = currentLang.startsWith('de');

            // Translate issues based on language
            return response.data.issues.map((issue: TransactionIssue) => {
                // Only translate in German, keep original in English
                if (isGerman) {
                    // Translation map for German
                    const germanTranslations: Record<string, string> = {
                        'Missing Cost Basis': 'Fehlende Kostenbasis',
                        'Missing Price': 'Fehlender Preis',
                        'Incomplete Transfer': 'Unvollständiger Transfer',
                        'Matched Transfer': 'Abgeglichener Transfer',
                    };

                    return {
                        ...issue,
                        description: germanTranslations[issue.description] || issue.description,
                    };
                }

                return issue;
            });
        } catch (error) {
            console.error('Error fetching transaction issues:', error);
            throw error;
        }
    };

    const groupMarkNonTaxable = async (params: Array<{ id: number; targetTypeId: TransactionTypeValue }>) => {
        try {
            await apiClient.post<void>(`${environment.ctLiteApiUrl}/transaction/group/mark-non-taxable`, {
                nonTaxableParams: params,
            });
        } catch (error) {
            console.error('Error marking transactions as non-taxable:', error);
            throw error;
        }
    };

    const getStats = async (year: string, selectedReportId: number, details: DetailsType = DetailsType.ONLY_ISSUES) => {
        try {
            const response = await apiClient.get<TransactionStats>(
                `${environment.ctLiteApiUrl}/transaction/stats?year=${year}&reportId=${selectedReportId}&details=${details}`
            );
            return response.data;
        } catch (error) {
            console.error(error);
            throw error;
        }
    };

    const getAllTimeIssueCount = async (details: DetailsType = DetailsType.ONLY_ISSUES) => {
        try {
            const response = await apiClient.get<any>(`${environment.ctLiteApiUrl}/transaction/all-time-issue-count?details=${details}`);
            return response.data;
        } catch (error) {
            console.error(error);
            throw error;
        }
    };

    const getById = async (id: number): Promise<Transaction | null> => {
        try {
            console.log(`[TransactionService] Fetching transaction by ID: ${id}`);
            
            // Try direct API call first (if the endpoint exists)
            try {
                const directResponse = await apiClient.get<Transaction>(`${environment.ctLiteApiUrl}/transaction/${id}`);
                if (directResponse.data) {
                    console.log(`[TransactionService] Found transaction ${id} via direct API`);
                    return transformTransaction({ data: [directResponse.data], total: 1, page: 0 }).data[0];
                }
            } catch (directError) {
                console.log(`[TransactionService] Direct API call failed for transaction ${id}, falling back to search`);
            }
            
            // Fallback: Search through transactions with a larger limit
            // We'll search in batches to find the transaction
            const batchSize = 500;
            let page = 0;
            const maxPages = 20; // Limit to prevent infinite loops (10,000 transactions max)
            
            while (page < maxPages) {
                const response = await get(page, batchSize, undefined, 'desc', 'date');
                
                if (!response.data || response.data.length === 0) {
                    // No more transactions to search
                    break;
                }
                
                // Find the exact transaction by ID from the results
                const transaction = response.data.find((tx: Transaction) => {
                    return tx.id === id || tx.id === id.toString() || tx.id.toString() === id.toString();
                });
                
                if (transaction) {
                    console.log(`[TransactionService] Found transaction ${id} on page ${page}`);
                    return transaction;
                }
                
                // If we got fewer results than the batch size, we've reached the end
                if (response.data.length < batchSize) {
                    break;
                }
                
                page++;
            }
            
            console.warn(`[TransactionService] Transaction ${id} not found after searching ${page * batchSize} transactions`);
            return null;
        } catch (error) {
            console.error('Error fetching transaction by ID:', error);
            throw error;
        }
    };

    return {
        get,
        getById,
        getCount,
        getUserAccounts,
        getUserCoins,
        getLiquidityPools,
        getLiquidityPoolNames,
        getTypes,
        getUserTypes,
        getTypesGrouped,
        getCoins,
        getFromTaxReports,
        save,
        groupSave,
        deleteAllByIds,
        recalculateInfo,
        recalculateStart,
        recalculateStop,
        checkSpam,
        markSpam,
        groupMarkSpam,
        groupRemoveSpam,
        clearSpamTransactions,
        removeSpam,
        getTransactionIssues,
        groupMarkNonTaxable,
        getStats,
        getAllTimeIssueCount,
    };
};
